import { Select, Flex, Typography, Tooltip } from "antd";
import {
  BankAccountGrid,
  BankGridDto,
  COMPANY_ROLES,
  COMPANY_TYPES,
  CompanyEntity,
  CompanyUserDto,
  CountryCodeGridDto,
  FiatCurrencyGridDto,
  UserListEntity,
} from "interfaces";
import { getFullName, getPhone } from "utils/formatNames";
import { CurrencyFlagRound, RadioButton } from "ui";

import styles from "./styles.module.css";

export const renderSelectOptions = (enumObject: any, iconCreator?: (key: string, value: string) => React.ReactNode) => {
  // Check if enumObject is an array (new format with icons)
  if (Array.isArray(enumObject)) {
    return enumObject.map((item) => (
      <Select.Option key={item.key} value={item.key}>
        <Flex align="center" gap="small">
          {item.icon}
          <Typography.Text>{item.value}</Typography.Text>
        </Flex>
      </Select.Option>
    ));
  }

  // Original format (Record<string, string>)
  return Object.keys(enumObject).map((key) => {
    const value = enumObject[key];

    if (iconCreator) {
      const icon = iconCreator(key, value);

      return (
        <Select.Option key={key} value={key}>
          <Flex align="center" gap="small">
            {icon}
            <Typography.Text>{value}</Typography.Text>
          </Flex>
        </Select.Option>
      );
    }

    return (
      <Select.Option key={key} value={key}>
        {value}
      </Select.Option>
    );
  });
};

export const renderBooleanSelectOptions = () => {
  const options = [
    { label: "Да", value: true },
    { label: "Нет", value: false },
  ];

  return options.map((option) => (
    <Select.Option key={option.label} value={option.value}>
      {option.label}
    </Select.Option>
  ));
};

export const renderBankOptions = (bankAccounts: BankAccountGrid[]) => {
  return bankAccounts
    ?.slice()
    .sort((a, b) => (a.isDefault ? -1 : 1))
    .map((item) => (
      <Select.Option key={item.id} value={item.id}>
        <Flex gap="small" align="center" className="overflow-hidden vertical-option">
          <Typography.Text className="overflow-hidden vertical-option__title">{item.accountName}</Typography.Text>
          <Typography.Text className="text-description text-small overflow-hidden">{item.bankName}</Typography.Text>
          <Typography.Text className="text-description text-small">{item.accountNumber}</Typography.Text>
        </Flex>
      </Select.Option>
    ));
};

export const renderCompaniesOptions = (companies: CompanyUserDto[]) => {
  return companies.map((company) => (
    <Select.Option key={company.companyName} value={company.id} searchFields={{ companyName: company.companyName }}>
      <Tooltip title={company.companyName}>{company.companyName}</Tooltip>
    </Select.Option>
  ));
};

export const renderBankListOptions = (banks: BankGridDto[]) => {
  return banks.map((item) => (
    <Select.Option key={item.bankName} value={item.bik} searchFields={{ bankName: item.bankName, bik: item.bik }}>
      <Flex gap="small" align="center" className="overflow-hidden">
        <Typography.Text>{item.bankName}</Typography.Text>
        <Typography.Text className="text-description text-small overflow-hidden">{item.bik}</Typography.Text>
      </Flex>
    </Select.Option>
  ));
};

export const renderCountryListOptions = (countries: CountryCodeGridDto[]) => {
  return countries.map((item) => (
    <Select.Option
      key={item.country}
      value={item.countryCode}
      searchFields={{ country: item.country, countryCode: item.countryCode }}
      label={
        <Flex align="center" gap="8px" className={`overflow-hidden ${styles.countryLabelWidth}`}>
           {/* flag /> */}
          <span className={`text-middle text-bold ${styles.mainText}`}>{item.country}</span>
          <span className={`text-middle text-bold form-text-color ${styles.code}`}>{item.countryCode}</span>
        </Flex>
      }
    >
      <Flex justify="space-between" align="center" className="full-width">
        <Flex className={`${styles.selectTextContainer} ${styles.countryContainerWidth}`}>
          <span className={`text-middle ${styles.mainText}`}>{item.country}</span>
        </Flex>
        <Flex>
          <span className={`text-middle form-text-color ${styles.code}`}>{item.countryCode}</span>
          {/* flag /> */}
        </Flex>
      </Flex>
    </Select.Option>
  ));
};

export const renderCompanyListOptions = (countries: CompanyEntity[]) => {
  return countries.map((item) => {
    const regLabel = item.type === COMPANY_TYPES.IP ? "ОГРНИП" : "ОГРН";
    return (
    <Select.Option
      key={item.id}
      value={item.id}
      label={
        <Flex align="center" gap="small" className="overflow-hidden">
          <Typography.Text className="text-small overflow-hidden">{item.companyName}</Typography.Text>
          <Typography.Text className="text-small text-description">
            {regLabel} {item.companyRegistrationNumber}
          </Typography.Text>
          <Typography.Text className="text-small text-description">ИНН {item.inn}</Typography.Text>
        </Flex>
      }
    >
      <Flex vertical>
        <Typography.Text className="text-small overflow-hidden">{item.companyName}</Typography.Text>
        <Flex align="center" gap="small" className="overflow-hidden">
          <Typography.Text className="text-small text-description">
            {regLabel} {item.companyRegistrationNumber}
          </Typography.Text>
          <Typography.Text className="text-small text-description">ИНН {item.inn}</Typography.Text>
          <Typography.Text className="text-small text-description">{COMPANY_ROLES.texts[item.role]}</Typography.Text>
        </Flex>
      </Flex>
    </Select.Option>
    );
  });
};

export const renderFiatCurrencyListOptions = (fiatCurrencyList: FiatCurrencyGridDto[]) => {
  return fiatCurrencyList.map((item) => (
    <Select.Option
      key={item.isoLat3}
      value={item.isoLat3}
      label={
        <Flex align="center" gap="8px">
          <CurrencyFlagRound currency={item.isoLat3} />
          <span className="text-middle text-bold">{item.isoLat3}</span>
        </Flex>
      }
      searchFields={{ isoLat3: item.isoLat3, nameRus: item.nameRus }}
    >
      <Flex justify="space-between" align="center" className="full-width">
        <Flex className={`${styles.selectTextContainer} ${styles.imgTextContainerWidth}`}>
          <span className={`text-middle text-bold ${styles.code}`}>{item.isoLat3}</span>
          <span className={`text-middle ${styles.mainText}`}>{item.nameRus}</span>
        </Flex>
        <CurrencyFlagRound currency={item.isoLat3} />
      </Flex>
    </Select.Option>
  ));
};

export const renderUserListOptions = (users: UserListEntity[]) => {
  const OptionContent = ({ user }: { user: UserListEntity }) => (
    <>
      <Typography.Text className="text-small overflow-hidden">{getFullName(user)}</Typography.Text>
      <Typography.Text className="text-small text-description">{getPhone(user.phone)}</Typography.Text>
    </>
  );

  return users.map((user) => (
    <Select.Option
      key={user.id}
      value={user.id}
      label={
        <Flex align="center" gap="small" className="overflow-hidden">
          <OptionContent user={user} />
        </Flex>
      }
    >
      <Flex vertical>
        <OptionContent user={user} />
      </Flex>
    </Select.Option>
  ));
};

export const getFirstOption = (enumObject: any) => Object.keys(enumObject)[0];

export const renderRadioButtons = (enumObject: any, className?: string) => {
  return Object.keys(enumObject).map((key, index, array) => (
    <RadioButton key={key} value={key} className={className || ""}>
      {enumObject[key]}
    </RadioButton>
  ));
};

export const filterOption = (input: string, option: any) => {
  const searchTerm = input.toLowerCase();
  const searchFields = option?.searchFields || {};

  if (Object.keys(searchFields).length > 0) {
    return Object.values(searchFields).some((field) => field && field.toString().toLowerCase().includes(searchTerm));
  }

  return false;
};
